import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { translations } from 'src/constants/translations';
import { BoxFormTextField } from 'src/components/profileComponents/FormTextField';
import { SubGroupWrapper } from 'src/components/profileComponents/Marketplace/components/SubGroupWrapper';
import {
  useStyles as mainUseStyles,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/Common/styles';
import {
  UploadMarketplace,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/UploadMarketplace';
import {
  IDevelopmentSpecificationPropChange,
} from 'src/components/profileComponents/Complexes/components/ProjectSpecification/types';
import {
  UnitTilePropertyType,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/constants';
import { BoxFormAutocomplete } from 'src/components/profileComponents/FormAutocomplete';
import {
  CardTagElementProperty,
  IMarketplacePlacementCardTagElement,
} from 'src/typings/marketplaceTile';
import {
  IMarketplace,
  IMarketplacePlacementProperty,
  Option,
} from 'src/components/profileComponents/Marketplace/types';
import { UnitStyle } from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/Common/components/UnitStyle';
import { updateTranslatedField } from 'src/components/profileComponents/Marketplace/components/Filters/helpers';
import { useMarketplaces } from 'src/hooks/marketplace';
import { readURL } from 'src/helpers';
import Tooltip from '@mui/material/Tooltip';
import Warning from '@mui/icons-material/Warning';

export const UnitsCardEntity: React.FC<{
  marketplace: IMarketplace | undefined,
  entity: IMarketplacePlacementCardTagElement,
  onChange: (entity: IMarketplacePlacementCardTagElement) => void,
  lang: string,
  propertyOptions: Option<IMarketplacePlacementProperty>[],
  moveToTrash: () => void
  isNeedToBeUpdated: boolean,
}> = ({
  marketplace, entity, onChange, lang, propertyOptions, moveToTrash, isNeedToBeUpdated,
}) => {
  const [image, setImage] = useState<string | null>(entity.iconFileUri?.fileUrl || null);
  const stylesConfig = ['fontWeight', 'fontDecoration', 'fontColor', 'backgroundColor', 'outlineColor'];
  const mainStyles = mainUseStyles();
  const [chooseValueOptions, setChooseValueOptions] = useState<Option<string>[]>([]);
  const [_isNeedToChange, setIsNeedToChange] = useState<boolean>(isNeedToBeUpdated);
  const { fetchMarketplaceLanguageObjects, marketplaceLanguageObjectsOptions, marketplaceLanguageObjectsIsLoading } = useMarketplaces();

  useEffect(() => {
    setIsNeedToChange(isNeedToBeUpdated);
  }, [isNeedToBeUpdated]);

  useEffect(() => {
    setChooseValueOptions(marketplaceLanguageObjectsOptions);
  }, [marketplaceLanguageObjectsOptions]);

  const onOpen = useCallback(async (findProperties: string[]) => {
    if (marketplace) {
      fetchMarketplaceLanguageObjects({
        marketplaceId: marketplace.id,
        findProperties,
      }, lang);
    }
  }, [lang]);

  return (
    <SubGroupWrapper
      name={translations.unitTag}
      onTrash={() => moveToTrash()}
      customOffsetStyles={mainStyles.customOffsetStyles}
    >
      <Box display="flex" gap="20px">
        <Box>
          <UploadMarketplace
            register={() => {}}
            registerName=""
            image={image}
            onUpload={async (file, response) => {
              onChange({ ...entity, iconFileUri: response.data[0] });
              const base64: string = await readURL(file);
              if (base64) {
                setImage(base64);
              }
            }}
            onMoveToTrash={() => {
              onChange({ ...entity, iconFileUri: null });
              setImage(null);
            }}
          />
        </Box>
        <Box display="flex" flexDirection="column" flex="auto">
          <Box display="flex" gap="20px">
            <div className={mainStyles.prefixSuffixWrapper}>
              <BoxFormTextField
                label={translations.prefix}
                name="prefix"
                defaultValue={entity.prefix?.[lang]}
                onChange={(event) => {
                  onChange(updateTranslatedField(entity, 'prefix', lang, event.target.value));
                }}
              />
            </div>
            <div className={mainStyles.selectWrapper}>
              <BoxFormAutocomplete
                label={translations.propertyName}
                name="property"
                propertyToCompare="systemName"
                defaultValue={{ label: entity.property?.translates?.[lang] || entity.property?.systemName, value: entity.property }}
                onChange={(event: IDevelopmentSpecificationPropChange<CardTagElementProperty>) => {
                  onChange({ ...entity, property: event.value });
                }}
                options={propertyOptions}
              />
            </div>
            <div className={mainStyles.prefixSuffixWrapper}>
              <BoxFormTextField
                label={translations.suffix}
                name="suffix"
                defaultValue={entity.suffix?.[lang]}
                onChange={(event) => {
                  onChange(updateTranslatedField(entity, 'suffix', lang, event.target.value));
                }}
              />
            </div>
            <div className={mainStyles.selectWrapper}>
              <BoxFormAutocomplete
                label={translations.propertyValue}
                name="propertyValue"
                defaultValue={{ label: entity.propertyValue?.[lang] || entity.property?.systemName, value: entity.propertyValue?.[lang] }}
                isLoading={marketplaceLanguageObjectsIsLoading}
                onOpen={() => onOpen([entity.property.systemName])}
                onClose={() => setChooseValueOptions([])}
                onChange={(event: IDevelopmentSpecificationPropChange<UnitTilePropertyType>) => {
                  onChange(updateTranslatedField(entity, 'propertyValue', lang, event.value));
                  setIsNeedToChange(false);
                }}
                options={chooseValueOptions}
              />
              { _isNeedToChange && (
              <Box display="flex" alignItems="center" marginRight={4}>
                <Tooltip title={<h3>{translations.isNeedToChange}</h3>} placement="top">
                  <Warning color="warning" />
                </Tooltip>
              </Box>
              ) }
            </div>
          </Box>
          <UnitStyle
            entityConfig={stylesConfig}
            entityStyles={entity.styles}
            updateProperty={(property, value) => {
              if (entity.styles) {
                onChange({ ...entity, styles: { ...entity.styles, [property]: value } });
              }
            }}
          />
        </Box>
      </Box>
    </SubGroupWrapper>
  );
};
